
import React from 'react';

const GameGuide = () => {
  return (
    <section className="my-16" id="game-guide">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-8 text-center text-purple-300">Time Shooter 3 Unblocked Game Guide</h2>
        
        <div className="bg-indigo-900/50 p-8 rounded-xl shadow-lg mb-12">
          <h3 className="text-2xl font-bold mb-6 text-purple-300">Getting Started</h3>
          
          <div className="space-y-6 text-indigo-100">
            <p>
              Time Shooter 3 Unblocked is an intense first-person shooter game that combines tactical gameplay with a unique time-manipulation mechanic. This comprehensive guide will help you master the game's mechanics and complete missions successfully.
            </p>
            
            <div className="bg-indigo-800/50 p-4 rounded-lg">
              <h4 className="text-xl font-semibold mb-3 text-purple-300">Basic Controls</h4>
              <ul className="list-disc list-inside space-y-2">
                <li><span className="text-purple-300 font-semibold">WASD</span> - Move your character</li>
                <li><span className="text-purple-300 font-semibold">Mouse</span> - Aim your weapon</li>
                <li><span className="text-purple-300 font-semibold">Left Mouse Button</span> - Fire weapon</li>
                <li><span className="text-purple-300 font-semibold">Right Mouse Button</span> - Aim down sights</li>
                <li><span className="text-purple-300 font-semibold">SHIFT</span> - Run</li>
                <li><span className="text-purple-300 font-semibold">CTRL</span> - Crouch</li>
                <li><span className="text-purple-300 font-semibold">SPACE</span> - Jump</li>
                <li><span className="text-purple-300 font-semibold">R</span> - Reload weapon</li>
                <li><span className="text-purple-300 font-semibold">F</span> - Interact with objects</li>
                <li><span className="text-purple-300 font-semibold">Q</span> - Switch weapons</li>
                <li><span className="text-purple-300 font-semibold">E</span> - Use equipment</li>
                <li><span className="text-purple-300 font-semibold">SHIFT + SPACE</span> - Tactical slide</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-xl font-semibold mb-3 text-purple-300">The Time Manipulation Mechanic</h4>
              <p className="mb-4">
                The defining feature of Time Shooter 3 Unblocked is the ability to slow down time. This creates strategic opportunities and allows for impressive combat maneuvers.
              </p>
              
              <div className="bg-indigo-800/50 p-4 rounded-lg mb-4">
                <ul className="list-disc list-inside space-y-2">
                  <li><span className="text-purple-300 font-semibold">T</span> - Activate time slowdown</li>
                  <li><span className="text-purple-300 font-semibold">Time Gauge</span> - Shows remaining slowdown time</li>
                  <li><span className="text-purple-300 font-semibold">Time Recharge</span> - The gauge refills automatically when not in use</li>
                </ul>
              </div>
              
              <p>
                When time slowdown is activated, you'll notice enemy movements become significantly slower, bullets become visible in the air (and can sometimes be dodged), and you'll have extra time to line up perfect shots. Use this ability strategically, as the time gauge depletes quickly during use.
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-8">
          <div className="bg-indigo-900/50 p-6 rounded-lg">
            <h3 className="text-xl font-bold mb-4 text-purple-300">Advanced Combat Techniques</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-purple-200">Tactical Time-Breach</h4>
                <p className="text-indigo-100">
                  A cornerstone technique in Time Shooter 3 is the "Time-Breach" maneuver. To perform it, slow down time just before entering a room, then breach and eliminate all targets before they can react. This is especially useful in hostage rescue scenarios where quick neutralization of threats is essential.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-purple-200">Precision Matrix Dodge</h4>
                <p className="text-indigo-100">
                  When facing multiple enemies, activate time slowdown and strafe between incoming bullets. This advanced technique requires practice but allows you to engage multiple opponents simultaneously while minimizing your risk of taking damage.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-purple-200">Time-Assisted Headshots</h4>
                <p className="text-indigo-100">
                  For maximum efficiency, use time slowdown to line up consecutive headshots on multiple targets. This conserves ammunition and ensures instant neutralization of threats. Focus on developing the muscle memory to quickly transition between targets during the slowdown effect.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-purple-200">Strategic Time Management</h4>
                <p className="text-indigo-100">
                  Your time gauge is limited and recharges slowly. Instead of using time slowdown continuously, use it in short bursts for critical moments. This "pulse" technique maximizes your time gauge's efficiency and ensures you have the ability available when truly needed.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-indigo-900/50 p-6 rounded-lg">
            <h3 className="text-xl font-bold mb-4 text-purple-300">Mission Strategy Guide</h3>
            
            <div className="space-y-4 text-indigo-100">
              <p>
                Each mission in Time Shooter 3 Unblocked presents unique challenges and requires specific approaches:
              </p>
              
              <div>
                <h4 className="font-semibold text-purple-200">Hostage Rescue Operations</h4>
                <p>
                  When rescuing hostages, prioritize eliminating immediate threats first. Use time slowdown to ensure your shots don't hit civilians. Move methodically through areas, clearing rooms completely before moving to the next. Remember that hostages can provide valuable intelligence when rescued, potentially revealing the location of additional threats or objectives.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-purple-200">Bomb Defusal Missions</h4>
                <p>
                  Time management is crucial in bomb defusal scenarios. Clear the path to the bomb location efficiently, then secure the area before beginning the defusal process. Be prepared for "last stand" enemy waves that often appear during the defusal countdown. Position yourself strategically and use time slowdown to handle multiple attackers during these critical moments.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-purple-200">Assault and Breach Operations</h4>
                <p>
                  For missions focused on eliminating targets, scout when possible before engaging. Use equipment like flash grenades before breaching rooms with multiple enemies. Time your slowdown ability to coincide with room entries for maximum advantage. Remember that some enemies wear armor that requires multiple body shots to penetrate, making headshots even more valuable.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-purple-200">Stealth Operations</h4>
                <p>
                  Some missions allow for stealth approaches. Use silenced weapons and take advantage of patrol patterns. Time slowdown can help you handle situations when stealth is broken. Pay attention to environmental noise triggers like glass and metal objects that can alert enemies to your presence.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-indigo-900/50 p-6 rounded-lg">
            <h3 className="text-xl font-bold mb-4 text-purple-300">Weapon Selection and Loadout Tips</h3>
            
            <div className="space-y-4 text-indigo-100">
              <p>
                Your weapon selection significantly impacts your mission success in Time Shooter 3 Unblocked:
              </p>
              
              <div>
                <h4 className="font-semibold text-purple-200">Close-Quarters Combat</h4>
                <p>
                  For indoor operations and tight spaces, shotguns and SMGs excel. The Combat Shotgun is particularly effective when combined with time slowdown, allowing you to engage multiple close-range targets in succession. The Tactical SMG provides better sustained fire capability when facing larger groups of enemies in confined spaces.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-purple-200">Mid-Range Engagements</h4>
                <p>
                  The Tactical Assault Rifle offers the best all-around performance for most mission scenarios. Its balance of accuracy, damage, and fire rate makes it adaptable to varying combat distances. When using time slowdown, switch to semi-automatic fire for improved accuracy and ammunition conservation.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-purple-200">Long-Range Operations</h4>
                <p>
                  The Precision Sniper Rifle is unmatched for long-distance engagements. When combined with time slowdown, you can eliminate multiple distant targets with headshots before they can react or take cover. Position yourself strategically at elevated positions to maximize the effectiveness of this approach.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-purple-200">Tactical Equipment Selection</h4>
                <p>
                  Don't underestimate the value of tactical equipment. Stun Grenades are essential for room clearing, temporarily disorienting enemies and allowing you to eliminate them without resistance. Breaching Charges provide alternative entry points that can give you a tactical advantage through surprise. Consider your mission objectives when selecting equipment to ensure you're properly prepared.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-indigo-900/50 p-6 rounded-lg">
            <h3 className="text-xl font-bold mb-4 text-purple-300">Performance Optimization Tips</h3>
            
            <p className="text-indigo-100 mb-4">
              Time Shooter 3 Unblocked is optimized for browser play, but you can enhance your experience with these tips:
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-indigo-100">
              <li>Close unnecessary browser tabs to allocate more resources to the game</li>
              <li>Use a modern browser like Chrome or Firefox for best performance</li>
              <li>Lower the game resolution if experiencing lag or stuttering</li>
              <li>Use a wired mouse for more precise aiming</li>
              <li>Consider using headphones for better audio awareness of enemy positions</li>
              <li>If available, use hardware acceleration in your browser settings</li>
              <li>Disable browser extensions that might consume resources while playing</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <a href="#play-now" className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-full transition-all transform hover:scale-105 hover:shadow-lg">
            Apply These Tips In-Game Now
          </a>
        </div>
      </div>
    </section>
  );
};

export default GameGuide;
